import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const BACKGROUNDS = {
    cream: REDESIGN_COLORS.cream,
    dark: REDESIGN_COLORS.dark,
    none: 'transparent',
};

const useStyles = makeStyles((theme) => ({
    root: ({ background, paddingTop, paddingBottom, overflow }) => ({
        position: 'relative',
        // Signed in, pages render as flex items of the drawer's row grid and
        // would otherwise shrink to their content, wrapping short headers.
        boxSizing: 'border-box',
        width: '100%',
        // Hidden clips the decorative blobs that bleed off the edges. A section
        // holding position: sticky children must pass 'visible': a clipping
        // ancestor becomes their scroll container and they never pin.
        overflow,
        background: BACKGROUNDS[background] || background,
        paddingTop,
        paddingBottom,
        // These sections are anchor targets (#top, #video, #dovezi...), and the
        // redesigned nav is sticky, so without this an anchor jump parks the
        // heading underneath the bar.
        scrollMarginTop: '96px',
        // The redesign's body type, matching the design source's `body` rule.
        // Set here so everything inside a section inherits it; the global theme
        // still carries the old scale for pages that have not been converted.
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '16px',
        lineHeight: 1.6,
        color: REDESIGN_COLORS.dark,
        // The design source has no media queries, so its vertical rhythm is fixed.
        // Tighten it on phones so sections do not read as mostly empty space.
        [theme.breakpoints.down('xs')]: {
            paddingTop: Math.round(paddingTop * 0.6),
            paddingBottom: Math.round(paddingBottom * 0.6),
        },
    }),
    inner: ({ maxWidth, gutter }) => ({
        position: 'relative',
        zIndex: 2,
        // This app has no global border-box reset, so any padding a consumer adds
        // via innerClassName would otherwise widen the column past its maxWidth.
        boxSizing: 'border-box',
        width: `min(${maxWidth}px, calc(100% - ${gutter}px))`,
        margin: '0 auto',
    }),
}));

/**
 * Outer shell for a landing section: full-bleed background plus the centred
 * content column the design uses throughout - min(1160px, 100% - 40px).
 * Hidden overflow and the positioned inner column are what let sections carry
 * the decorative blobs that bleed off the edges; `overflow="visible"` is for
 * sections with sticky content instead.
 */
const LandingSection = ({
    id,
    background = 'cream',
    paddingTop = 48,
    paddingBottom = 64,
    maxWidth = 1160,
    gutter = 40,
    overflow = 'hidden',
    className,
    innerClassName,
    children,
}) => {
    const classes = useStyles({ background, paddingTop, paddingBottom, maxWidth, gutter, overflow });

    return (
        <section id={id} className={[classes.root, className].filter(Boolean).join(' ')}>
            <div className={[classes.inner, innerClassName].filter(Boolean).join(' ')}>{children}</div>
        </section>
    );
};

export default LandingSection;
